import React, { useState } from "react";
import PropTypes from "prop-types";
import { Card, Button, Select } from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { toastError } from "../../../shared/toastHelper";
import { buildSeatPills } from "../../../shared/formattingUtils";

export const seatLabel = (seat) => buildSeatPills([seat]).join(" · ");

const fits = (seat, ticket) => seat.ticket_ids.includes(ticket.id);

export const initialPicks = (tickets, seats) => {
  const picks = {};
  tickets.forEach((ticket) => {
    const options = seats.filter((seat) => fits(seat, ticket));
    if (options.length !== 1) return;
    const [only] = options;
    const sharedWithAnother = tickets.some((other) => other.id !== ticket.id && fits(only, other));
    if (!sharedWithAnother) picks[ticket.id] = String(only.id);
  });
  return picks;
};

const SeatLinkPicker = ({ seatPicker, seatLinksPath }) => {
  const { tickets, seats, needed } = seatPicker;
  const [picks, setPicks] = useState(() => initialPicks(tickets, seats));
  const [saving, setSaving] = useState(false);

  const chosen = Object.values(picks).filter(Boolean);
  const canSave = chosen.length === needed && new Set(chosen).size === chosen.length && !saving;

  const optionsFor = (ticket) => [
    { value: "", label: needed < tickets.length ? "No seat (sell as General Admission)" : "Choose a seat" },
    ...seats
      .filter((seat) => fits(seat, ticket))
      .filter((seat) => picks[ticket.id] === String(seat.id) || !chosen.includes(String(seat.id)))
      .map((seat) => ({ value: String(seat.id), label: seatLabel(seat) })),
  ];

  const save = async () => {
    setSaving(true);
    try {
      const pairs = Object.fromEntries(Object.entries(picks).filter(([, seatId]) => seatId));
      await client.post(seatLinksPath, { pairs });
      window.location.reload();
    } catch (error) {
      toastError(error?.response?.data?.errors?.join(" ") || "Something went wrong. Please try again.");
      setSaving(false);
    }
  };

  return (
    <Card padding="md">
      <div className="resale-card-body" data-testid="seat-link-picker">
        <h3 className="resale-card-title">Match your seats to your tickets</h3>
        <p className="resale-step-subtitle">
          Your reserved seats aren&apos;t tied to your tickets yet, so pick the seat that goes with each one. You only do this once.
        </p>
        <div className="resale-ticket-list">
          {tickets.map((ticket, index) => (
            <div key={ticket.id} className="resale-ticket-row resale-seat-link-row" data-testid="seat-link-row">
              <Select
                id={`seat-link-${ticket.id}`}
                label={`${ticket.name} · Ticket ${index + 1}`}
                options={optionsFor(ticket)}
                value={picks[ticket.id] || ""}
                onChange={(value) => setPicks({ ...picks, [ticket.id]: value == null ? "" : String(value) })}
              />
            </div>
          ))}
        </div>
        {!canSave && !saving && (
          <p className="resale-ticket-owner" data-testid="seat-link-hint">
            {needed === tickets.length ? "Pick a seat for each ticket to save." : `Pick a seat for ${needed} of your tickets to save.`}
          </p>
        )}
        <div className="resale-actions-right">
          <Button variant="primary" disabled={!canSave} onClick={save}>
            Save my seats
          </Button>
        </div>
      </div>
    </Card>
  );
};

SeatLinkPicker.propTypes = {
  seatPicker: PropTypes.shape({
    needed: PropTypes.number.isRequired,
    waiting_ticket_ids: PropTypes.arrayOf(PropTypes.number).isRequired,
    tickets: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })).isRequired,
    seats: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      section: PropTypes.string,
      row: PropTypes.string,
      seat: PropTypes.string,
      ticket_ids: PropTypes.arrayOf(PropTypes.number).isRequired,
    })).isRequired,
  }).isRequired,
  seatLinksPath: PropTypes.string.isRequired,
};

export default SeatLinkPicker;
